import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Users, Factory, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";

const WORKSHOP_FILTER_LABELS = {
  all: "الكل",
  approved: "معتمدة",
  pending: "قيد المراجعة",
  rejected: "مرفوضة",
};

export function WorkshopsAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/workshops").then((r) => setItems(r.data.workshops || []));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((w) =>
    (filter === "all" || w.status === filter) &&
    (!q || (w.workshop_name || "").includes(q) || (w.city || "").includes(q))
  );

  const approve = async (id) => { await api.post(`/workshops/${id}/approve`); toast.success("تم اعتماد الورشة"); load(); };
  const reject = async (id) => { await api.post(`/workshops/${id}/reject`); toast.success("تم رفض الورشة"); load(); };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">الورش</div>
        <h1 className="text-3xl font-bold text-brand-navy">إدارة الورش</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="ابحث..." value={q} onChange={(e) => setQ(e.target.value)} className="border-0 focus-visible:ring-0 flex-1 min-w-40" data-testid="workshops-search" />
        <div className="flex gap-2">
          {["all", "approved", "pending", "rejected"].map((s) => (
            <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={filter === s ? "btn-cta" : ""} data-testid={`workshops-filter-${s}`}>
              {WORKSHOP_FILTER_LABELS[s]}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2" data-testid="workshops-grid">
        {filtered.map((w) => (
          <div key={w.id} className="card-industrial p-5" data-testid={`workshop-card-${w.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-bold text-brand-navy">{w.workshop_name}</div>
                <div className="text-xs text-slate-500">{w.city} • {w.industrial_area}</div>
              </div>
              <StatusBadge status={w.status} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div><span className="text-slate-500">التقييم:</span> {w.quality_rating}/5</div>
              <div><span className="text-slate-500">دقة التسليم:</span> {(w.on_time_rate * 100).toFixed(0)}%</div>
              <div><span className="text-slate-500">الموظفين:</span> {w.employees}</div>
              <div><span className="text-slate-500">التأسيس:</span> {w.year_established}</div>
              <div className="col-span-2"><span className="text-slate-500">القدرات:</span> {(w.capabilities || []).length}</div>
            </div>
            {w.status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" className="btn-cta flex-1" onClick={() => approve(w.id)} data-testid={`approve-workshop-${w.id}`}><CheckCircle2 className="w-4 h-4 me-1" /> اعتماد</Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => reject(w.id)} data-testid={`reject-workshop-${w.id}`}><XCircle className="w-4 h-4 me-1" /> رفض</Button>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">لا توجد نتائج.</div>}
      </div>
    </DashboardLayout>
  );
}

export function UsersAdmin() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data.users || [])); }, []);
  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">المستخدمون</div>
        <h1 className="text-3xl font-bold text-brand-navy">إدارة المستخدمين</h1>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="px-4 py-3 text-start">الاسم</th>
                <th className="px-4 py-3 text-start">البريد</th>
                <th className="px-4 py-3 text-start">الدور</th>
                <th className="px-4 py-3 text-start">الحالة</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">{u.role}</td>
                  <td className="px-4 py-3">{u.is_active ? "نشط" : "موقوف"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function FactoriesAdmin() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users", { params: { role: "factory_customer" } }).then((r) => setUsers(r.data.users || [])); }, []);
  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">المصانع</div>
        <h1 className="text-3xl font-bold text-brand-navy">المصانع والعملاء</h1>
      </div>
      <div className="grid gap-3 md:grid-cols-2" data-testid="factories-grid">
        {users.map((u) => (
          <div key={u.id} className="card-industrial p-5" data-testid={`factory-card-${u.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <Factory className="w-6 h-6 text-brand-amber" />
              <div>
                <div className="font-bold text-brand-navy">{u.company_name}</div>
                <div className="text-xs text-slate-500">{u.industry}</div>
              </div>
            </div>
            <div className="text-sm space-y-1">
              <div><span className="text-slate-500">المسؤول:</span> {u.name}</div>
              <div><span className="text-slate-500">المدينة:</span> {u.city}</div>
              <div><span className="text-slate-500">السجل التجاري:</span> {u.cr_number}</div>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

export function AnalyticsPage() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/analytics/summary").then((r) => setS(r.data)); }, []);
  const stats = [
    { label: "إجمالي الطلبات", value: s.total_requests, color: "text-brand-amber" },
    { label: "الطلبيات النشطة", value: s.active_orders, color: "text-blue-600" },
    { label: "الطلبيات المكتملة", value: s.completed_orders, color: "text-emerald-600" },
    { label: "الورش المعتمدة", value: s.approved_workshops, color: "text-purple-600" },
    { label: "الورش قيد المراجعة", value: s.pending_workshops, color: "text-amber-600" },
    { label: "المصانع", value: s.total_factories, color: "text-slate-600" },
    { label: "إجمالي المبيعات (ر.س)", value: s.total_revenue?.toLocaleString?.(), color: "text-emerald-700" },
    { label: "إجمالي تكلفة الورش (ر.س)", value: s.total_workshop_cost?.toLocaleString?.(), color: "text-slate-700" },
    { label: "الربح الإجمالي (ر.س)", value: s.gross_profit?.toLocaleString?.(), color: "text-brand-amber" },
  ];
  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">التحليلات</div>
        <h1 className="text-3xl font-bold text-brand-navy">تقارير وتحليلات</h1>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="analytics-grid">
        {stats.map((st) => (
          <div key={st.label} className="card-industrial p-5">
            <div className="text-sm text-slate-500">{st.label}</div>
            <div className={`text-3xl font-bold mt-2 ${st.color}`}>{st.value ?? "—"}</div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
